/**
 * Soil analysis catalogue and arithmetic. Mirrors HHNodeServer
 * src/utils/soilAnalytes.js, which is authoritative for anything saved (it
 * converts units and derives the figures every read shows). This copy labels
 * the form and powers the maintenance figures for a typed yield. Both pin the
 * same worked example (soilAnalytes.test.ts / soilAnalytes.test.js).
 */

import type { RemovalNutrient, RemovalRates, SampleType, SoilAnalyteGroup, SoilDerived, SoilResult } from '../types/soil';

export interface SoilAnalyte {
  key: string;
  label: string;
  unit: string;
  group: SoilAnalyteGroup;
  guide?: { low?: number; high?: number };
  /** Soil unless said otherwise. */
  type?: SampleType;
}

const CATION = 'cmol(+)/kg';

export const SOIL_ANALYTES: SoilAnalyte[] = [
  { key: 'ph_cacl2', label: 'pH (CaCl₂)', unit: '', group: 'chemistry', guide: { low: 5.5, high: 7.0 } },
  { key: 'ph_water', label: 'pH (water)', unit: '', group: 'chemistry', guide: { low: 6.0, high: 7.5 } },
  { key: 'ec', label: 'Electrical conductivity (1:5)', unit: 'dS/m', group: 'chemistry' },
  { key: 'organic_carbon', label: 'Organic carbon', unit: '%', group: 'chemistry' },
  { key: 'organic_matter', label: 'Organic matter', unit: '%', group: 'chemistry' },
  { key: 'nitrate_n', label: 'Nitrate nitrogen', unit: 'mg/kg', group: 'nutrients' },
  { key: 'ammonium_n', label: 'Ammonium nitrogen', unit: 'mg/kg', group: 'nutrients' },
  { key: 'phosphorus_colwell', label: 'Phosphorus (Colwell)', unit: 'mg/kg', group: 'nutrients' },
  { key: 'phosphorus_olsen', label: 'Phosphorus (Olsen)', unit: 'mg/kg', group: 'nutrients' },
  { key: 'pbi', label: 'Phosphorus buffering index', unit: '', group: 'nutrients' },
  { key: 'potassium_colwell', label: 'Potassium (Colwell)', unit: 'mg/kg', group: 'nutrients' },
  { key: 'sulfur', label: 'Sulfur (KCl-40)', unit: 'mg/kg', group: 'nutrients' },
  { key: 'chloride', label: 'Chloride', unit: 'mg/kg', group: 'nutrients' },
  { key: 'calcium', label: 'Exchangeable calcium', unit: CATION, group: 'cations' },
  { key: 'magnesium', label: 'Exchangeable magnesium', unit: CATION, group: 'cations' },
  { key: 'potassium', label: 'Exchangeable potassium', unit: CATION, group: 'cations' },
  { key: 'sodium', label: 'Exchangeable sodium', unit: CATION, group: 'cations' },
  { key: 'aluminium', label: 'Exchangeable aluminium', unit: CATION, group: 'cations' },
  { key: 'cec', label: 'Cation exchange capacity (ECEC)', unit: CATION, group: 'cations' },
  { key: 'zinc', label: 'Zinc (DTPA)', unit: 'mg/kg', group: 'trace' },
  { key: 'copper', label: 'Copper (DTPA)', unit: 'mg/kg', group: 'trace' },
  { key: 'manganese', label: 'Manganese (DTPA)', unit: 'mg/kg', group: 'trace' },
  { key: 'iron', label: 'Iron (DTPA)', unit: 'mg/kg', group: 'trace' },
  { key: 'boron', label: 'Boron (hot CaCl₂)', unit: 'mg/kg', group: 'trace' },

  // Leaf tissue, dry weight.
  { key: 'leaf_n', label: 'Nitrogen', unit: '%', group: 'leaf_macro', type: 'leaf' },
  { key: 'leaf_p', label: 'Phosphorus', unit: '%', group: 'leaf_macro', type: 'leaf' },
  { key: 'leaf_k', label: 'Potassium', unit: '%', group: 'leaf_macro', type: 'leaf' },
  { key: 'leaf_ca', label: 'Calcium', unit: '%', group: 'leaf_macro', type: 'leaf' },
  { key: 'leaf_mg', label: 'Magnesium', unit: '%', group: 'leaf_macro', type: 'leaf' },
  { key: 'leaf_s', label: 'Sulfur', unit: '%', group: 'leaf_macro', type: 'leaf' },
  { key: 'leaf_na', label: 'Sodium', unit: '%', group: 'leaf_macro', type: 'leaf' },
  { key: 'leaf_cl', label: 'Chloride', unit: '%', group: 'leaf_macro', type: 'leaf' },
  { key: 'leaf_b', label: 'Boron', unit: 'mg/kg', group: 'leaf_trace', type: 'leaf' },
  { key: 'leaf_zn', label: 'Zinc', unit: 'mg/kg', group: 'leaf_trace', type: 'leaf' },
  { key: 'leaf_cu', label: 'Copper', unit: 'mg/kg', group: 'leaf_trace', type: 'leaf' },
  { key: 'leaf_mn', label: 'Manganese', unit: 'mg/kg', group: 'leaf_trace', type: 'leaf' },
  { key: 'leaf_fe', label: 'Iron', unit: 'mg/kg', group: 'leaf_trace', type: 'leaf' },
  { key: 'leaf_mo', label: 'Molybdenum', unit: 'mg/kg', group: 'leaf_trace', type: 'leaf' },
];

export const analyteType = (a: SoilAnalyte): SampleType => a.type ?? 'soil';
export const analytesOf = (type: SampleType) => SOIL_ANALYTES.filter((a) => analyteType(a) === type);
export const LEAF_ANALYTES = analytesOf('leaf');

/** The groups a sample type's results are shown in, in order. */
export const GROUPS: Record<SampleType, SoilAnalyteGroup[]> = {
  soil: ['chemistry', 'nutrients', 'cations', 'trace'],
  leaf: ['leaf_macro', 'leaf_trace'],
};

export const SAMPLE_TYPE_LABEL: Record<SampleType, string> = { soil: 'Soil', leaf: 'Leaf tissue' };

export const ANALYTE_BY_KEY = new Map(SOIL_ANALYTES.map((a) => [a.key, a]));
export const OTHER = 'other';

export const GROUP_LABEL: Record<SoilAnalyteGroup, string> = {
  chemistry: 'Soil chemistry',
  nutrients: 'Nutrients',
  cations: 'Exchangeable cations',
  trace: 'Trace elements',
  leaf_macro: 'Major nutrients (dry weight)',
  leaf_trace: 'Trace elements (dry weight)',
};

/** Derived figures with a general guide: sodic above 6 % ESP; aluminium above 5 % of the ECEC. */
export const DERIVED_GUIDE = { esp: { high: 6 }, al_saturation: { high: 5 } };

/** Shown wherever a guide verdict is. */
export const LEAF_NOT_ADVICE = 'Standards are the ranges your organisation set for this crop, read against leaves sampled the way they assume. They are not a recommendation.';

export const NOT_ADVICE = 'Guide ranges are general figures for tree crops, not a recommendation. Read them with your agronomist and the lab\'s own interpretation.';

export function round(v: number | null | undefined, dp = 2): number | null {
  if (v == null || !Number.isFinite(v)) return null;
  const f = 10 ** dp;
  return Math.round(v * f) / f;
}

export function derive(results: Pick<SoilResult, 'analyte' | 'value'>[]): SoilDerived {
  const v: Record<string, number> = {};
  for (const r of results) if (r.analyte !== OTHER && v[r.analyte] == null) v[r.analyte] = Number(r.value);
  const cations = ['calcium', 'magnesium', 'potassium', 'sodium', 'aluminium'];
  const present = cations.filter((k) => Number.isFinite(v[k]));
  let ecec: number | null = Number.isFinite(v.cec) ? v.cec : null;
  let source: SoilDerived['ecec_source'] = ecec != null ? 'reported' : null;
  if (ecec == null && ['calcium', 'magnesium', 'potassium'].every((k) => present.includes(k))) {
    ecec = present.reduce((s, k) => s + v[k], 0);
    source = 'sum';
  }
  const pct = (k: string) => (ecec && Number.isFinite(v[k]) ? round((v[k] / ecec) * 100, 1) : null);
  const esp = pct('sodium');
  return {
    ecec: round(ecec, 2),
    ecec_source: source,
    ca_mg_ratio: Number.isFinite(v.calcium) && v.magnesium > 0 ? round(v.calcium / v.magnesium, 1) : null,
    esp,
    al_saturation: pct('aluminium'),
    ca_pct: pct('calcium'),
    mg_pct: pct('magnesium'),
    k_pct: pct('potassium'),
    na_pct: esp,
  };
}

export type GuideVerdict = 'low' | 'high' | 'ok' | null;

export function guideVerdict(guide: { low?: number; high?: number } | undefined, value: number | null | undefined): GuideVerdict {
  if (!guide || value == null || !Number.isFinite(value)) return null;
  if (guide.low != null && value < guide.low) return 'low';
  if (guide.high != null && value > guide.high) return 'high';
  return 'ok';
}

export const REMOVAL_NUTRIENTS: { key: RemovalNutrient; label: string; per: 'kg' | 'g' }[] = [
  { key: 'N', label: 'Nitrogen', per: 'kg' },
  { key: 'P', label: 'Phosphorus', per: 'kg' },
  { key: 'K', label: 'Potassium', per: 'kg' },
  { key: 'Ca', label: 'Calcium', per: 'kg' },
  { key: 'Mg', label: 'Magnesium', per: 'kg' },
  { key: 'S', label: 'Sulfur', per: 'kg' },
  { key: 'B', label: 'Boron', per: 'g' },
  { key: 'Zn', label: 'Zinc', per: 'g' },
  { key: 'Cu', label: 'Copper', per: 'g' },
  { key: 'Mn', label: 'Manganese', per: 'g' },
  { key: 'Fe', label: 'Iron', per: 'g' },
];

export interface MaintenanceLine {
  nutrient: RemovalNutrient;
  label: string;
  unit: string;
  per_ha: number | null;
  total: number | null;
  total_unit: 'kg' | 'g';
}

/** Removal per tonne × yield: per hectare, and over the area when it has hectares. */
export function maintenance(rates: RemovalRates, tPerHa: number, areaHa: number | null): MaintenanceLine[] {
  if (!Number.isFinite(tPerHa) || tPerHa < 0) return [];
  return REMOVAL_NUTRIENTS
    .filter((n) => Number.isFinite(rates[n.key]))
    .map((n) => {
      const r = rates[n.key] as number;
      const dp = n.per === 'kg' ? 1 : 0;
      return {
        nutrient: n.key,
        label: n.label,
        unit: `${n.per}/ha`,
        per_ha: round(r * tPerHa, dp),
        total: areaHa != null && areaHa > 0 ? round(r * tPerHa * areaHa, dp) : null,
        total_unit: n.per,
      };
    });
}

/** A result for display: "<0.5", "7.2". */
export function showValue(r: Pick<SoilResult, 'value' | 'qualifier'>): string {
  return `${r.qualifier ?? ''}${fmtNum(r.value)}`;
}

export function fmtNum(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  const dp = abs >= 100 ? 0 : abs >= 10 ? 1 : abs >= 1 ? 2 : 3;
  return String(round(v, dp));
}

export function depthText(from: number | null, to: number | null): string | null {
  if (from == null && to == null) return null;
  return `${fmtNum(from ?? 0)}–${fmtNum(to)} cm`;
}

export function resultLabel(r: Pick<SoilResult, 'analyte' | 'label'>): string {
  return ANALYTE_BY_KEY.get(r.analyte)?.label ?? r.label ?? r.analyte;
}

// ─── Trends ───────────────────────────────────────────────────────────────────

export interface TrendPoint {
  sampleId: number;
  date: string;
  /** Days since 1970-01-01, so the x axis spaces readings by real time. */
  day: number;
  value: number;
  qualifier: '<' | '>' | null;
}

type TrendSample = {
  id: number;
  sampled_on: string;
  sample_type?: SampleType;
  cultivar_area_id: number | null;
  depth_from_cm: number | null;
  depth_to_cm: number | null;
  results: SoilResult[];
};

export const dayNumber = (iso: string) => Math.round(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / 86_400_000);
export const dayToIso = (day: number) => new Date(day * 86_400_000).toISOString().slice(0, 10);

/**
 * The samples a trend is drawn from: one sample type, one place (that
 * planting, or the block as a whole when areaId is null), and for soil one
 * depth (the given one, else the depth most samples were taken at). A 0–10 cm
 * and a 10–30 cm reading on one line would read as a change that is not one.
 */
export function trendSamples<T extends TrendSample>(
  samples: T[], type: SampleType, areaId: number | null, depth?: { from: number | null; to: number | null },
): T[] {
  const here = samples.filter((s) => (s.sample_type ?? 'soil') === type && (s.cultivar_area_id ?? null) === areaId);
  if (type === 'leaf') return here;
  const key = (s: { depth_from_cm: number | null; depth_to_cm: number | null }) => `${s.depth_from_cm ?? ''}-${s.depth_to_cm ?? ''}`;
  let want = depth ? `${depth.from ?? ''}-${depth.to ?? ''}` : null;
  if (!want) {
    const counts = new Map<string, number>();
    for (const s of here) counts.set(key(s), (counts.get(key(s)) ?? 0) + 1);
    want = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
  }
  return here.filter((s) => key(s) === want);
}

/** One analyte's readings, oldest first. */
export function trendPoints(samples: TrendSample[], analyte: string): TrendPoint[] {
  return samples
    .map((s) => {
      const r = s.results.find((x) => x.analyte === analyte);
      return r ? { sampleId: s.id, date: s.sampled_on, day: dayNumber(s.sampled_on), value: r.value, qualifier: r.qualifier } : null;
    })
    .filter((p): p is TrendPoint => !!p)
    .sort((a, b) => a.day - b.day || a.sampleId - b.sampleId);
}

/** Catalogue analytes with at least two readings across the samples, in catalogue order. */
export function trendAnalytes(samples: TrendSample[], type: SampleType): SoilAnalyte[] {
  return analytesOf(type).filter((a) => samples.filter((s) => s.results.some((r) => r.analyte === a.key)).length >= 2);
}
